import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import TableSkeleton from '../../../components/common/TableSkeleton';
import NoDataRow from '../../../components/common/NoDataRow';
import StatusChip from '../../../components/Billing/Invoice/InvoiceStatusChip';
import { TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import { formatDateDMY } from '../../../utils/dateUtils';
import { storePrevRoute } from '../../../utils/commonUtils';
import { ReactComponent as PrintIcon } from '../../../assets/SVG/printer-blue.svg';
import IconButton from '@mui/material/IconButton';
import ReusableReportComponent from '../../../components/printReports/ReusableReportComponent';
import DrugDispense from './NewPatientPharmacy/DispenseListReport';
import { useExistingPatientPrescriptions } from './newPharmacy/newPharmacyForm.helper';

export interface PharmacyTableData {
  date: string;
  patient_id: string | number;
  patientName: string;
  patientAge?: string | number;
  patientMrn?: string;
  patientGender?: string;
  provider: string;
  invoice_id: number | string;
  prescriptionStatus: 'ordered' | 'unordered' | string;
  appointment_id?: number | null;
  id?: number | null;
  dispense_number?: string;
}

interface PharmacyTableProps {
  data: PharmacyTableData[];
  loading?: boolean;
  showPatientColumn?: boolean;
}

/** Table labels */
const TABLE_LABELS = {
  noData: 'No pharmacy records found.',
  loading: 'Loading...',
  date: 'Date',
  patientName: 'Patient Name / Age',
  provider: 'Provider',
  dispenseNumber: 'Dispense Number',
  prescriptionStatus: 'Prescription Status',
  Action: 'Action',
};

const headerCells: Array<{
  key: string;
  label: string;
  minWidth?: number;
  width?: string;
  align?: 'center' | 'left' | 'right';
}> = [
  { key: 'date', label: TABLE_LABELS.date, minWidth: 120 },
  { key: 'patientName', label: TABLE_LABELS.patientName, minWidth: 250 },
  { key: 'provider', label: TABLE_LABELS.provider, minWidth: 150 },
  { key: 'dispenseNumber', label: TABLE_LABELS.dispenseNumber, minWidth: 150 },
  { key: 'prescriptionStatus', label: TABLE_LABELS.prescriptionStatus, minWidth: 160 },
  { key: 'Action', label: TABLE_LABELS.Action, minWidth: 100, align: 'center' },
];

const commonCellSx = {
  verticalAlign: 'middle' as const,
  px: 2.5,
  py: 2,
};

/** Normalize prescription status for display */
const normalizePrescriptionStatus = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'ordered') return 'Ordered';
  if (s === 'dispensed') return 'Dispensed';
  return 'Unordered';
};

function PharmacyCheckedInTable({ data, loading = false, showPatientColumn = true }: PharmacyTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrint, setShowPrint] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<PharmacyTableData | null>(null);

  const {
    data: prescriptionsData,
  } = useExistingPatientPrescriptions(
    selectedRow?.appointment_id,
    selectedRow?.invoice_id ? (selectedRow.invoice_id as number) : undefined
  );

  const visibleHeaderCells = React.useMemo(
    () => (showPatientColumn ? headerCells : headerCells.filter((h) => h.key !== 'patientName')),
    [showPatientColumn]
  );
  

  // Handle row click
  const handleRowClick = (row: PharmacyTableData) => {
    storePrevRoute(location.pathname, location.search);

    navigate(`/admin/billing/pharmacy/new/${row.patient_id}`, {
      state: { appointment_id: row.appointment_id ?? null , invoice_id: row.invoice_id ?? null},
    });

  };

  // Print report handler
  const printReport = (row: PharmacyTableData) => {
    setSelectedRow(row);
    setShowPrint(true);
  };

  const handleClose = () => {
    setShowPrint(false);
    setSelectedRow(null);
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
      <TableContainer
        component={Paper}
        sx={{ minWidth: 360, boxShadow: 'none', border: 'none' }}
      >
        <Table
          size="medium"
          aria-label="pharmacy table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& th, & td': {
              borderBottom: '1px solid',
              borderColor: '#F0F0F0',
            },
          }}
        >
          <TableHead>
            <TableRow sx={TABLE_HEADER_ROW_SX}>
              {visibleHeaderCells.map((h, index) => (
                <TableCell
                  key={h.key}
                  align={h.align}
                  sx={{
                    ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                    ...(h.width ? { width: h.width } : {}),
                    whiteSpace: 'nowrap',
                    px: 2.5,
                    ...(index === 0 ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : {}),
                    ...(index === visibleHeaderCells.length - 1 ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 } : {}),
                  }}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableSkeleton
                columnWidths={showPatientColumn ? [120, 250, 150, 150, 160] : [120, 150, 150, 160]}
                rows={10}
              />
            ) : data && data.length > 0 ? (
              data.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{
                    height: 64,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                  }}
                >
                  <TableCell sx={{ ...commonCellSx, minWidth: 120 }}>
                    {row.date ? formatDateDMY(row.date) : '-'}
                  </TableCell>
                  {showPatientColumn && (
                    <TableCell sx={{ ...commonCellSx, minWidth: 250 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#232323' }}>
                          {row.patientName}{row.patientAge ? ` / ${row.patientAge}` : ''}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#3B82F6', 
                            display: 'block', 
                            mt: 0.25,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {row.patientMrn}{row.patientMrn && row.patientGender ? ' | ' : ''}{row.patientGender}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell sx={{ ...commonCellSx, minWidth: 150 }}>
                    {row.provider || '-'}
                  </TableCell>
                  <TableCell sx={{ ...commonCellSx, minWidth: 150 }}>
                    {row.dispense_number || '-'}
                  </TableCell>
                  <TableCell sx={{ ...commonCellSx, minWidth: 160 }}>
                    <StatusChip status={normalizePrescriptionStatus(row.prescriptionStatus)} width={140} height={28} />
                  </TableCell>
                  <TableCell sx={{ ...commonCellSx, minWidth: 160, textAlign: 'center' }}>
                    <IconButton
                      aria-label="print report"
                      onClick={e => {
                        e.stopPropagation();
                        printReport(row);
                      }}
                      size="small"
                    >
                      <PrintIcon style={{ width: 20, height: 20 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <NoDataRow message={TABLE_LABELS.noData} colSpan={visibleHeaderCells.length} />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ReusableReportComponent
        open={showPrint}
        onClose={handleClose}
        reportTitle={'Dispensed Drug List'}
      >
        <DrugDispense DrugListData={prescriptionsData} />
      </ReusableReportComponent>
    </Box>
  );
}

export default PharmacyCheckedInTable;
